import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import useTimezone from "../../../../hooks/useTimezone";

import { DATE_FORMATS } from "../../../../constants/global.constants";
import { LICENSE_STATUS } from "../../../../constants/LicenseManagement.constants";

import {
  CorrectIcon,
  PendingIcon,
  TerminatedIcon,
  UnassignedIcon,
  WrongIcon,
} from "../../../../assets/icons";

import CustomTooltip from "../../../../common/styles/components/tooltip/CustomTooltip";

const LicenseStatusCellRenderer = ({
  data,
  colDef,
  licenseType,
  licenseId,
  node,
}) => {
  const { t } = useTranslation();
  const { formatTime } = useTimezone();
  const { expiringSoon, expired, active, unassigned, terminated } =
    LICENSE_STATUS;
  const actualLicenseId =
    typeof licenseId !== "undefined" ? licenseId : Number(colDef.field);
  const actualRowIndex = node?.rowIndex;
  const license = useMemo(
    () =>
      Array.isArray(data.licenses)
        ? data.licenses.find((l) => l && l.id === actualLicenseId)
        : null,
    [data.licenses, actualLicenseId],
  );

  const rawStatusText = license?.licenseStatus || unassigned;
  const statusDisplayMap = {
    ExpiringSoon: t("licenseManagement.filterExpiringSoon"),
    Expired: t("licenseManagement.filterExpired"),
    Active: t("licenseManagement.filterActive"),
    Unassigned: t("licenseManagement.filterUnassigned"),
    Terminated: t("licenseManagement.filterTerminated"),
  };
  const displayStatusText = statusDisplayMap[rawStatusText] || rawStatusText;

  const tooltipContent = useMemo(
    () => (
      <div className="license-status-tooltip-content">
        {displayStatusText}
        {license?.expiryDate && (
          <>
            <br />
            <div className="license-status-tooltip-expiry">
              {rawStatusText === terminated
                ? t("licenseManagement.terminated")
                : rawStatusText === expired
                  ? t("licenseManagement.expired")
                  : t("licenseManagement.expires")}
              {" - "}
              {formatTime(license?.expiryDate, true, DATE_FORMATS.MMM_DD_YYYY)}
            </div>
          </>
        )}
      </div>
    ),
    [displayStatusText, rawStatusText, license],
  );

  const statusIconMap = {
    [expiringSoon]: <PendingIcon />,
    [expired]: <WrongIcon />,
    [active]: <CorrectIcon />,
    [unassigned]: <UnassignedIcon />,
    [terminated]: <TerminatedIcon />,
  };
  const licenseStatusIcon = useMemo(
    () => statusIconMap[rawStatusText] || <UnassignedIcon />,
    [rawStatusText],
  );

  return (
    <div
      className="license-status-cell-center"
      id={`license-cell-status-${licenseType}-${actualLicenseId}-row-${actualRowIndex}`}
    >
      <CustomTooltip text={tooltipContent} placement="bottom">
        <div className="license-status-icon-tooltip-fix">
          {licenseStatusIcon}
        </div>
      </CustomTooltip>
    </div>
  );
};

export default LicenseStatusCellRenderer;
